import React from 'react'
import { motion } from 'framer-motion'
import { BarChart2, Target, Heart, Lightbulb, Users, Award } from 'lucide-react'
import Hero from '../components/Hero'
import TeamMember from '../components/TeamMember'

const About = () => {
  return (
    <>
      <Hero 
        title="À propos d'AMOGOTech"
        subtitle="Découvrez notre histoire, notre mission et les personnes qui font d'AMOGOTech un leader des solutions technologiques en Afrique."
        imageSrc="https://itservicesgroupe.fr/wp-content/uploads/it-services-groupe-informatique-installation-maintenance-et-depannage-de-systemes-informatique-pour-les-professionnels-1024x682-155.webp"
        imageAlt="L'équipe AMOGOTech"
      />

      {/* Our Story Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <h2 className="section-title">Notre histoire</h2>
              <p className="text-lg text-gray-600 mb-6">
                Fondée en 2018 à Dakar, AMOGOTech est née de la vision de trois ingénieurs sénégalais passionnés par la technologie et déterminés à créer des solutions adaptées aux défis spécifiques du marché africain.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Face aux problèmes récurrents de navigation et de cartographie au Sénégal, notre premier projet, Route Senegal, a rapidement connu un succès important auprès des entreprises de logistique et de transport.
              </p>
              <p className="text-lg text-gray-600">
                Aujourd'hui, AMOGOTech compte plus de 30 collaborateurs talentueux et continue d'innover pour répondre aux besoins technologiques croissants du continent africain.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-xl blur-xl opacity-70"></div>
              <img 
                src="https://st2.depositphotos.com/3994509/10684/i/380/depositphotos_106845306-stock-photo-black-man-speaks-into-microphone.jpg" 
                alt="L'histoire d'AMOGOTech" 
                className="relative rounded-xl shadow-xl w-full h-auto object-cover z-10"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Values Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title text-black" >Notre mission et nos valeurs</h2>
              <p className="section-subtitle">
                Chez AMOGOTech, nous sommes guidés par une mission claire et des valeurs fortes qui définissent notre approche et notre culture d'entreprise.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-white rounded-xl shadow-md p-8"
            >
              <div className="inline-flex items-center justify-center p-3 bg-primary-100 rounded-lg text-primary-600 mb-6">
                <Target size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-black">Notre mission</h3>
              <p className="text-gray-600">
                Développer des solutions technologiques innovantes et accessibles qui répondent aux défis spécifiques du marché africain, contribuant ainsi à la transformation numérique du continent.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-white rounded-xl shadow-md p-8"
            >
              <div className="inline-flex items-center justify-center p-3 bg-secondary-100 rounded-lg text-secondary-600 mb-6">
                <Heart size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-black">Notre vision</h3>
              <p className="text-gray-600">
                Devenir le leader des solutions technologiques en Afrique, en créant des produits qui améliorent la vie quotidienne des populations et favorisent le développement économique durable.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <div className="inline-flex items-center justify-center p-3 bg-primary-100 rounded-lg text-primary-600 mb-4">
                <Lightbulb size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-black">Innovation</h3>
              <p className="text-gray-600">
                Nous encourageons la créativité et l'exploration de nouvelles idées pour résoudre les problèmes complexes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <div className="inline-flex items-center justify-center p-3 bg-primary-100 rounded-lg text-primary-600 mb-4">
                <Users size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-black">Collaboration</h3>
              <p className="text-gray-600">
                Nous croyons en la puissance du travail d'équipe et de la diversité des perspectives pour atteindre l'excellence.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <div className="inline-flex items-center justify-center p-3 bg-primary-100 rounded-lg text-primary-600 mb-4">
                <Award size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-black">Excellence</h3>
              <p className="text-gray-600">
                Nous nous engageons à fournir des produits et services de la plus haute qualité, dépassant les attentes de nos clients.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Notre équipe de direction</h2>
              <p className="section-subtitle">
                Découvrez les personnes passionnées qui dirigent AMOGOTech et contribuent à son succès.
              </p>
            </motion.div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <TeamMember name="Moussa Thiam" role="Co-fondateur et Gerant" imageSrc="https://scontent.fdkr7-1.fna.fbcdn.net/v/t39.30808-6/464384167_9012987512085558_8295631589280549154_n.jpg?_nc_cat=105&ccb=1-7&_nc_sid=aa7094&_nc_eui2=AeG3SQAQFUZopvN0kT94_FeiF-QXHun-5KkX5Bce6f7kqX_d94YUl7uk7bcNe_eJpiWzxk93diaXKJ67dDv2GpO2&_nc_ohc=ciC7lncAx_MQ7kNvgGpJWpw&_nc_oc=Adh33Se7lHWvDObMHjYRYAtxari6a3UkZ0QgBZSHyIkpclS5skikmhn5UyR4TUZlg78&_nc_zt=23&_nc_ht=scontent.fdkr7-1.fna&_nc_gid=AmMIc6vvoO-Wo7IRyQ-xurX&oh=00_AYG8Avwsz0EwUHKdeGhZGQSfmXm5e-rWmKfEJ4jjfSazug&oe=67D4A6C0" />
            <TeamMember name="Amadou Dramé" role="Co-fondateur et CTO" imageSrc="https://media.licdn.com/dms/image/v2/D4E03AQFjnXqoHKf4RQ/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1677856574017?e=1747267200&v=beta&t=UL5YO0wIJBtH6hpkv1yNRUnTelo4BEiMmdPxMJF5KJI" />
            <TeamMember name="Gora Mbaye Keita" role="Co-fondateur et COO" imageSrc="COO1.png" />
          </div>
        </div>
      </section>
    </>
  )
}

export default About
